import { FormattedMessage, useIntl } from "react-intl";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { BiChevronUp } from "react-icons/bi";
import DataTable from "react-data-table-component";
import {
  customStyles,
  EmptyContainer,
  HighlightText,
  StatusBadge,
  TableContainer,
} from "../../styles";
import { useMemo, useState, useCallback } from "react";
import Button from "../Button";
import api from "../../../../service";
import generateHeaders from "../../../../utils/generateHeaders";
import { useSelector } from "react-redux";
import JSZip from "jszip";
import { Modal } from "antd";
import {
  ModalButtonsWrapper,
  ModalSubtitle,
  ModalTitle,
} from "../../../InformaAcademy/styles";
import { TableButtonContainer } from "../../../Manual/style";
import { useToast } from "../../../../hooks/toast";
import { useLoading } from "../../../../hooks/loading";

export default function ReportTable({
  reportData = [],
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  setSortBy,
  setSortDirection,
  updateMediaKitStatus,
}) {
  const intl = useIntl();
  const columns = useMemo(
    () => [
      {
        id: "contract",
        name: <FormattedMessage id={"contract"} />,
        selector: (row) => row.contract ?? "-",
        sortable: true,
        maxWidth: "120px",
      },
      {
        id: "name",
        name: <FormattedMessage id={"admin_ai_creator.name"} />,
        selector: (row) => row.name,
        sortable: true,
      },
      {
        id: "email",
        name: <FormattedMessage id={"admin_ai_creator.responsible"} />,
        selector: (row) => row.email ?? "-",
        sortable: true,
        maxWidth: "250px",
      },
      {
        id: "origin",
        name: <FormattedMessage id={"admin_ai_creator.origin"} />,
        selector: (row) => row.origin ? intl.formatMessage({ id: row.origin }) : "-",
        sortable: true,
        maxWidth: "150px",
      },
      {
        id: "status",
        name: <FormattedMessage id={"admin_ai_creator.status"} />,
        selector: (row) => <Status created={row.created} />,
        sortable: false,
        maxWidth: "160px",
      },
      {
        id: "actionRow",
        selector: (row) => (
          <ActionRow
            opportunityId={row.opportunityId}
            created={row.created}
            contractNumber={row.contract}
            updateMediaKitStatus={updateMediaKitStatus}
          />
        ),
        maxWidth: intl.locale === "en" ? "280px" : "260px",
        minWidth: intl.locale === "en" ? "280px" : "260px",
      },
    ],
    [intl.locale],
  );

  const paginationOptions = {
    ...options({
      locale: intl.locale,
    }),
    disableNext: currentPage * rowsPerPage >= totalRows,
  };

  return (
    <TableContainer>
      <DataTable
        data={reportData}
        responsive
        noDataComponent={<Empty />}
        columns={columns}
        customStyles={customStyles}
        sortIcon={<BiChevronUp style={{ display: "flex" }} />}
        paginationComponentOptions={paginationOptions}
        persistTableHead
        pagination
        paginationServer
        paginationPerPage={rowsPerPage}
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(size) => {
          const newPage =
            Math.floor(((currentPage - 1) * rowsPerPage) / size) + 1;
          setCurrentPage(newPage);
          setRowsPerPage(size);
        }}
        paginationTotalRows={totalRows}
        onSort={(column, sortDirection) => {
          setSortBy(column.id);
          setSortDirection(sortDirection);
        }}
      />
    </TableContainer>
  );
}

const options = ({ locale }) => {
  return {
    rowsPerPageText: <FormattedMessage id={"rows_per_page"} />,
    rangeSeparatorText: locale === "en" ? "of" : "de",
    noRowsPerPage: false,
    selectAllRowsItem: false,
    selectAllRowsItemText: <FormattedMessage id={"all"} />,
  };
};

function Empty({ highlightText = "admin_ai_creator.empty" }) {
  return (
    <EmptyContainer>
      <AiOutlineExclamationCircle size={30} />
      <HighlightText>
        <FormattedMessage id={highlightText} />
      </HighlightText>
    </EmptyContainer>
  );
}

function Status({ created }) {
  return (
    <StatusBadge status={created ? "created" : "pending"}>
      <FormattedMessage id={created ? "created" : "pending"} />
    </StatusBadge>
  );
}

function ActionRow({
  opportunityId,
  created,
  contractNumber,
  updateMediaKitStatus,
}) {
  const [loading, setLoading] = useState(false);
  const { isLoading, handleLoading } = useLoading();
  const { token } = useSelector((state) => state.auth);
  const [modalOpen, setModalOpen] = useState(false);
  const { addToast } = useToast();
  const intl = useIntl();

  const handleDownload = async () => {
    const shapeTypes = ["square", "vertical", "horizontal"];
    const dimensions = ["1080x1350", "1080x1920", "1200x628"];

    try {
      setLoading(true);
      const response = await api.get(
        `/media-kit/admin/${opportunityId}`,
        generateHeaders(token),
      );
      const { data } = response;
      const mediaKit = [
        data.imageSquareUrl,
        data.imageVerticalUrl,
        data.imageHorizontalUrl,
      ];

      const zip = new JSZip();

      await Promise.all(
        mediaKit.map(async (imageData, index) => {
          if (!imageData) return;

          let blob;
          if (imageData.startsWith("data:")) {
            const base64Data = imageData.split(",")[1];
            blob = await fetch(`data:image/png;base64,${base64Data}`).then(
              (res) => res.blob(),
            );
          } else {
            blob = await fetch(imageData).then((res) => res.blob());
          }

          zip.file(
            `mediakit_${shapeTypes[index]}_${dimensions[index]}.png`,
            blob,
          );
        }),
      );

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = "mediakit_images.zip";
      link.click();
      URL.revokeObjectURL(link.href);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error downloading images:", error);
    }
  };

  const handleDelete = () => {
    deleteMediaKit();
    setModalOpen(false);
  };

  const deleteMediaKit = async () => {
    try {
      setLoading(true);
      handleLoading(true);
      await api.delete(
        `/media-kit/admin/${opportunityId}`,
        generateHeaders(token),
      );
      addToast({
        type: "success",
        title: intl.formatMessage({
          id: "success",
        }),
        description: intl.formatMessage({
          id: "success.media_kit_deleted",
        }),
      });
      updateMediaKitStatus(opportunityId, false, "");
    } catch (error) {
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: "api.error." + error?.response?.data?.code,
        }),
      });
    } finally {
      setLoading(false);
      handleLoading(false);
    }
  };

  return (
    <TableButtonContainer>
      <Button
        theme={"secondary"}
        size={"sm"}
        Icon={"download"}
        label={"admin_ai_creator.download"}
        noShadow
        onClick={handleDownload}
        disabled={loading || !created}
      />
      <Button
        theme={"destructive"}
        size={"sm"}
        Icon={"trash"}
        label={"delete"}
        noShadow
        onClick={() => setModalOpen(true)}
        disabled={loading || !created}
      />
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={"590px"}
        footer={""}
        closable={false}
        className="modalMediaKitTableDelete"
      >
        <ModalTitle>
          {intl.formatMessage(
            {
              id: "delete_media_kit_question"
            },
            {
              opportunity: `${contractNumber}`,
            }
          )}
        </ModalTitle>
        <ModalSubtitle bold>
          {intl.formatMessage({
            id: "irreversible_action",
          })}
        </ModalSubtitle>
        <ModalButtonsWrapper>
          <Button
            theme={"secondary"}
            size={"md"}
            Icon={"back"}
            label={"goback"}
            noShadow
            onClick={() => setModalOpen(false)}
          />
          <Button
            theme={"destructive2"}
            size={"md"}
            Icon={"trash"}
            label={"delete"}
            noShadow
            onClick={handleDelete}
          />
        </ModalButtonsWrapper>
      </Modal>
    </TableButtonContainer>
  );
}
